/**
 * Black Box — transport. Buffers events and ships them to the collector.
 *
 * - Periodic flush (interval or buffer-size) via fetch(keepalive).
 * - Final flush on pagehide / tab-hidden via navigator.sendBeacon.
 * - visitor id: our OWN first-party anonymous id, persisted in localStorage, so we
 *   can link a returning visitor across sessions WITHOUT depending on the host
 *   site's identifier. Sent as `visitor_id` (the collector's link field).
 */
export function createTransport(config, session) {
  let buffer = [];
  let timer = null;

  // Read-or-create our first-party visitor id (cached for the session).
  let _vid;
  function visitorId() {
    if (_vid !== undefined) return _vid;
    try {
      const k = config.visitorIdKey || 'bb_vid';
      let id = localStorage.getItem(k);
      if (!id) {
        id = 'v_' + Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 6);
        localStorage.setItem(k, id);
      }
      _vid = id;
    } catch (_) { _vid = null; }
    return _vid;
  }

  // Web click id from the checkout SDK (written to localStorage after its
  // traffic call, i.e. possibly after our first flush) — re-read until found.
  var _wcid = null;
  function wcid() {
    if (_wcid) return _wcid;
    try { _wcid = localStorage.getItem('nj_clickId') || null; } catch (_) { /* blocked storage */ }
    return _wcid;
  }

  function payload(events, final) {
    return JSON.stringify({
      session_id: session.id,
      key: config.siteKey || undefined,
      visitor_id: visitorId(),
      wcid: wcid() || undefined,
      context: session.context,
      final: !!final,
      events,
    });
  }

  // `beacon` = true ONLY on real page unload (pagehide), where fetch is unreliable.
  // Everything else — including finalize-on-success / idle-timeout while the page is
  // alive — uses fetch, which works cross-origin (sendBeacon cross-origin is flaky
  // and blocked by ad blockers). All sends are text/plain: CORS-safelisted, so no
  // preflight; the server parses it as JSON.
  function flush(final, beacon) {
    if (!buffer.length && !final) return;
    const events = buffer;
    buffer = [];
    const body = payload(events, final);

    if (beacon && navigator.sendBeacon) {
      try {
        if (navigator.sendBeacon(config.endpoint, new Blob([body], { type: 'text/plain' }))) return;
      } catch (_) {}
    }

    try {
      fetch(config.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body,
        keepalive: true,
      }).catch(function () {});
    } catch (_) {}
  }

  function push(event) {
    buffer.push(event);
    if (buffer.length >= config.flushMaxEvents) flush(false);
  }

  // Keep-alive ping (empty events) so the server's last_seen advances even when a
  // visitor is present but not interacting — lets a passive session get a real
  // dwell time instead of 0. Sent only while the tab is visible (see tracer.js).
  function heartbeat() {
    try {
      fetch(config.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: payload([], false),
        keepalive: true,
      }).catch(function () {});
    } catch (_) {}
  }

  function start() {
    timer = setInterval(function () { flush(false); }, config.flushIntervalMs);
  }

  function stop() {
    if (timer) clearInterval(timer);
    timer = null;
  }

  return { push, flush, heartbeat, start, stop };
}
